/**
 * Comprehensive input validation schemas using Zod
 * Provides sanitization and validation for all user inputs
 */

import { invokePublicEdgeFunction } from "@/lib/edgeFunctionHelpers";

// Re-export everything from the shared schema file
export * from "../../supabase/functions/_shared/validation-schemas";

/**
 * Async DNS validation using edge function
 * Validates email domain has valid MX records using DNS-over-HTTPS
 * Returns graceful fallback on errors to avoid blocking users
 * 
 * NOTE: This function remains here because it depends on invokePublicEdgeFunction
 * which is a client-side helper.
 */
export async function validateEmailDomainDNS(email: string): Promise<{
  isValid: boolean;
  message?: string;
  source?: string;
  resolver?: string;
}> {
  try {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) {
      return { isValid: false, message: "Invalid email format" };
    }

    const { data, error } = await invokePublicEdgeFunction('validate-email-domain', {
      body: { domain }
    });

    if (error) {
      console.error('DNS validation error:', error);
      // Graceful degradation - allow email if service is unavailable
      return { isValid: true, message: "DNS validation unavailable" };
    }

    return {
      isValid: data?.is_valid ?? true,
      message: data?.validation_message,
      source: data?.source,
      resolver: data?.resolver_used,
    };
  } catch (error) {
    console.error('DNS validation exception:', error);
    // Graceful degradation - don't block users if DNS validation fails
    return { isValid: true, message: "DNS validation error" };
  }
}
